import 'client-only'

import { CookieNamesEnum } from '@/enums/CookieNamesEnum'
import { SiteContextModeEnum } from '@/enums/SiteContextModeEnum'
import { RoleValueEnum } from '@/gen/bff'
import { UserCustomerTypeEnum } from '@/gen/ecom'
import { getCookie } from '@/helpers/getCookie'
import { getCustomerType } from '@/helpers/getCustomerType'
import { getMarket } from '@/helpers/getMarket'
import { getSelectedOrgUnitId } from '@/helpers/getSelectedOrgUnitId'
import { getUserId } from '@/helpers/getUserId'
import { getCachedSession, refreshCachedSession } from '@/lib/clients/getCachedSession'
import { env } from '@/lib/env'
import { resolveTenant } from '@/lib/tenant/resolve-tenant'
import { resolveSiteContext } from '@/lib/tenant/resolveSiteContext'

import type { Session } from 'next-auth'
import type { CreateClientConfig } from '@/gen/bff/client.gen'
import type { Tenant } from '@/lib/tenant/resolve-tenant'

const CUSTOMER_SCOPE_HEADERS = ['X-CustomerId', 'X-OrgUnit'] as const

const setAnonymousHeaders = (headers: Headers, tenant: Tenant) => {
  const siteContext =
    tenant.features.b2b && tenant.features.b2c
      ? resolveSiteContext(tenant, getCookie(CookieNamesEnum.Site)?.toLowerCase())
      : undefined

  for (const header of CUSTOMER_SCOPE_HEADERS) {
    headers.delete(header)
  }
  headers.set('X-Market', getMarket(tenant.id, siteContext))
  headers.set('X-Role', RoleValueEnum.ANONYMOUS)
}

const setAuthenticatedHeaders = (headers: Headers, tenant: Tenant, session: Session) => {
  if (!session.user?.customerType) {
    return
  }

  const customerTypeKey = getCustomerType(session).toLowerCase() as keyof typeof SiteContextModeEnum

  // A caller-supplied X-CustomerId/X-OrgUnit (batched loaders pin these) must win over the ambient
  // session/cookie value, so a queued request keeps targeting its own scope.
  if (!headers.has('X-CustomerId')) {
    headers.set('X-CustomerId', getUserId(session) ?? '')
  }
  headers.set(
    'X-Role',
    session.user.customerType === UserCustomerTypeEnum.Employee.toLocaleLowerCase()
      ? RoleValueEnum.EMPLOYEE
      : RoleValueEnum.CUSTOMER
  )
  headers.set('X-Market', getMarket(tenant.id, SiteContextModeEnum[customerTypeKey]))

  const selectedOrgUnitId = getSelectedOrgUnitId(session)

  if (selectedOrgUnitId && tenant.features.organisation && !headers.has('X-OrgUnit')) {
    headers.set('X-OrgUnit', selectedOrgUnitId)
  }
}

export const createClientConfig: CreateClientConfig = (config) => {
  const resolveAccessToken = async () => {
    const session = await getCachedSession()
    if (session?.access_token) {
      return session.access_token
    }

    // TODO: Get anonymous token via http request to the SAP /token endpoint
    return env.NEXT_PUBLIC_BFF_ANONYMOUS_TOKEN ?? ''
  }

  return {
    ...config,
    baseUrl: '/api/bff',
    auth: resolveAccessToken,
    // We are forced to override fetch to add custom headers
    fetch: async (input: string | URL | Request, init?: RequestInit) => {
      const session = await getCachedSession()
      const headers = new Headers(init?.headers)

      const tenant = resolveTenant(window.location.hostname)

      if (!session) {
        setAnonymousHeaders(headers, tenant)
      } else {
        setAuthenticatedHeaders(headers, tenant, session)
      }

      const response = await fetch(input, { ...init, headers })

      if (response.status !== 401 || !session?.access_token) {
        return response
      }

      // BFF gives no reason header for a 401 (unlike SAP's www-authenticate), so refresh once before giving up.
      const refreshedSession = await refreshCachedSession()

      if (!refreshedSession?.access_token || refreshedSession.error) {
        if (!window.location.pathname.endsWith('/relogin')) {
          window.location.href = '/relogin'
        }

        return response
      }

      const retryHeaders = new Headers(init?.headers)
      setAuthenticatedHeaders(retryHeaders, tenant, refreshedSession)
      retryHeaders.set('Authorization', `Bearer ${refreshedSession.access_token}`)

      return fetch(input, { ...init, headers: retryHeaders })
    },
  }
}
